import Papa from "papaparse";
import { z } from "zod";

export const GENDER_VALUES = ["男性", "女性"] as const;
export type Gender = (typeof GENDER_VALUES)[number];

export const LANG_VALUES = ["日本語", "英語", "中国語", "ベトナム語", "ポルトガル語"] as const;
export type Lang = (typeof LANG_VALUES)[number];

/**
 * Respondent — 回答者 1 名分の取り込み単位。
 *
 * 社労士入力の必須列: 氏名 / 社員番号 / 性別 / メールアドレス / 部署名。部署名は列自体は必須だがセルの値は任意。
 * 言語は任意列で、空欄は日本語とみなす (ADR-0040 Decision 1)。
 * 連絡方法は検証のみで Respondent に持たせない (永続化は QR 解禁時、ADR-0062)。
 * WFJ 生成列 (ID 番号 / 会社名称) はシステム側で付与するため CSV には含めない。
 */
export const respondentSchema = z.object({
  fullName: z
    .string()
    .min(1, "氏名を入力してください")
    .max(40, "氏名は 40 文字以内で入力してください"),
  employeeNumber: z.string().min(1, "社員番号を入力してください"),
  gender: z.enum(GENDER_VALUES, {
    error: () => "性別は「男性」「女性」のいずれかです",
  }),
  email: z.string().email("メールアドレスの形式が正しくありません"),
  department: z.string().max(40, "部署名は 40 文字以内で入力してください"),
  lang: z.enum(["", ...LANG_VALUES], {
    error: () => "言語は「日本語」「英語」「中国語」「ベトナム語」「ポルトガル語」のいずれかです",
  }),
});

export type Respondent = z.infer<typeof respondentSchema>;

/** 空欄を日本語として表示する (ADR-0040 Decision 1)。 */
export function langLabel(lang: Respondent["lang"]): Lang {
  return lang === "" ? "日本語" : lang;
}

export type ParseError =
  | { kind: "file"; message: string }
  | { kind: "row"; rowNumber: number; message: string };

export interface ParseResult {
  rows: Respondent[];
  errors: ParseError[];
}

const HEADER_FULL_NAME = "氏名";
const HEADER_EMPLOYEE_NUMBER = "社員番号";
const HEADER_GENDER = "性別";
const HEADER_CONTACT_METHOD = "連絡方法";
const HEADER_EMAIL = "メールアドレス";
const HEADER_DEPARTMENT = "部署名";
const HEADER_LANG = "言語";

// 連絡方法列が受理する唯一の値 (ADR-0062 D3)。
const CONTACT_METHOD_MAIL = "メール";

// REQUIRED_HEADERS に HEADER_LANG を含めない: 言語列は任意であり、列自体が無い名簿も
// 受理する (ADR-0040 Decision 1、既存顧客の名簿ファイルを壊さないため)。
const REQUIRED_HEADERS = [
  HEADER_FULL_NAME,
  HEADER_EMPLOYEE_NUMBER,
  HEADER_GENDER,
  HEADER_EMAIL,
  HEADER_DEPARTMENT,
] as const;

/**
 * parseRespondentsCsv — CSV File を読み 1 行ずつバリデートして {rows, errors} を返す。
 *
 *  - ヘッダ前後の空白と先頭 BOM (Excel が UTF-8 で吐く際の典型) は許容
 *  - 空行 (全セル空) はスキップしてエラーにしない
 *  - 行レベルのエラーは 1-based の rowNumber 付き (ヘッダを除いた CSV 内の論理行)
 *  - メールアドレス・社員番号の重複は 2 件目以降を行エラーにする (メールは小文字化して比較)
 */
export async function parseRespondentsCsv(file: File): Promise<ParseResult> {
  const text = await file.text();
  const parsed = Papa.parse<Record<string, string>>(text, {
    header: true,
    skipEmptyLines: "greedy",
    transformHeader: (h) => h.replace(/^\uFEFF/, "").trim(),
    transform: (value) => value.trim(),
  });

  const fields = parsed.meta.fields ?? [];
  const missing = REQUIRED_HEADERS.filter((h) => !fields.includes(h));
  if (missing.length > 0) {
    const list = missing.map((h) => `「${h}」`).join("");
    return {
      rows: [],
      errors: [{ kind: "file", message: `必須列${list}が見つかりません` }],
    };
  }

  const rows: Respondent[] = [];
  const errors: ParseError[] = [];
  const seenEmails = new Set<string>();
  const seenEmployeeNumbers = new Set<string>();
  let rowNumber = 0;

  for (const raw of parsed.data) {
    const fullName = (raw[HEADER_FULL_NAME] ?? "").trim();
    const employeeNumber = (raw[HEADER_EMPLOYEE_NUMBER] ?? "").trim();
    const gender = (raw[HEADER_GENDER] ?? "").trim();
    const contactMethod = (raw[HEADER_CONTACT_METHOD] ?? "").trim();
    const email = (raw[HEADER_EMAIL] ?? "").trim();
    const department = (raw[HEADER_DEPARTMENT] ?? "").trim();
    const lang = (raw[HEADER_LANG] ?? "").trim();

    // 「全セル空」は skipEmptyLines:"greedy" でほぼ落ちるが、空白だけ並んだ行は通るので明示的に弾く
    if (!fullName && !employeeNumber && !gender && !contactMethod && !email && !department && !lang)
      continue;

    rowNumber += 1;

    const result = respondentSchema.safeParse({
      fullName,
      employeeNumber,
      gender,
      email,
      department,
      lang,
    });
    if (!result.success) {
      // 1 行に対して最初のエラーのみ表示 (ユーザー側の認知負荷を抑える)
      const message = result.error.issues[0]?.message ?? "入力内容が正しくありません";
      errors.push({ kind: "row", rowNumber, message });
      continue;
    }

    if (contactMethod !== "" && contactMethod !== CONTACT_METHOD_MAIL) {
      errors.push({ kind: "row", rowNumber, message: "連絡方法は現在「メール」のみ指定できます" });
      continue;
    }

    if (seenEmployeeNumbers.has(result.data.employeeNumber)) {
      errors.push({ kind: "row", rowNumber, message: "社員番号が重複しています" });
      continue;
    }
    const emailKey = result.data.email.toLowerCase();
    if (seenEmails.has(emailKey)) {
      errors.push({ kind: "row", rowNumber, message: "メールアドレスが重複しています" });
      continue;
    }

    seenEmployeeNumbers.add(result.data.employeeNumber);
    seenEmails.add(emailKey);
    rows.push(result.data);
  }

  return { rows, errors };
}
